if (!customElements.get('expected-delivery')) {
  class ExpectedDelivery extends HTMLElement {
    constructor() {
      super();

      // Receive the data from the liquid
      this.shopifyLocation = this.dataset.currentLocation;
      this.shopifyLanguage =
        this.dataset.currentLanguage != 'en-dev' ? this.dataset.currentLanguage : 'en';
      this.ipLocationServiceUrl = this.dataset.ipLocationServiceUrl;
      this.countriesData = JSON.parse(this.dataset.objectData);

      // Elements
      this.expectedDeliveryEl = this.querySelector('[data-expected-delivery]');
      this.expectedDeliveryTextEl = this.querySelector('[data-expected-delivery-text]');
      this.expectedDeliveryText = this.expectedDeliveryTextEl.dataset.expectedDeliveryText;

      // Return if no default country data is found
      if (!this.countriesData.length || !this.expectedDeliveryEl) {
        return;
      }

      // Set the countryData based on the market
      const countryData = this.getTheCountryData();

      // Set the delivery data
      this.deliveryData = this.getDeliveryData(countryData);

      // Set the delivery date based on the shopify market
      this.setDeliveryDate();

      // Get the ip location to update the delivery data based on the IP
      this.fetchIPLocation().then((ipData) => {
        if (!ipData) return;

        // Set the country data from the countries shopify object
        const countryData = this.getTheCountryData(ipData);

        // Set the delivery data
        this.deliveryData = this.getDeliveryData(countryData);

        // Set the delivery date
        this.setDeliveryDate();
      });
    }

    /**
     * Fetch the ip location
     */
    async fetchIPLocation() {
      try {
        return await fetch(this.ipLocationServiceUrl)
          .then((response) => {
            return response.json();
          })
          .then((json) => {
            return json;
          });
      } catch (error) {
        // If no ip data is found we use the default delivery data
        return false;
      }
    }

    /**
     * Set the delivery data
     * @param {Object} ipData - The data object containing the IP location data.
     */
    getTheCountryData(ipData = {}) {
      // Get the delivery data from the ip location
      let countryData = this.countriesData.find(
        (location) => location.countryCode === ipData?.countryCode
      );

      // Get the default delivery data from the shopify location if no match is found
      if (!countryData) {
        countryData = this.countriesData.find(
          (location) => location.countryCode === this.shopifyLocation
        );
      }

      // Get the default delivery data if no match is found
      if (!countryData) {
        countryData = this.countriesData.find((data) => !data.defaultCountry == true)
          ? this.countriesData.find((data) => !data.defaultCountry == true)
          : this.countriesData[0];
      }

      // If ipData then use the timezone from the ipData
      if (ipData) {
        countryData.timeZone = ipData.timezone;
      }

      // Format the skipDays to an array
      if (typeof countryData.noDelivery == 'string') {
        countryData.noDelivery = countryData.noDelivery.split(',');
      }
      if (typeof countryData.noShipping == 'string') {
        countryData.noShipping = countryData.noShipping.split(',');
      }

      return countryData;
    }

    /**
     * Set the delivery data
     * @param {Object} countryData - The data object containing the country data.
     * @returns {Object} - The delivery data object.
     */
    getDeliveryData(countryData) {
      if (!countryData) {
        return;
      }

      // Set the delivery data
      const deliveryData = {
        deliveryDays: parseInt(countryData.days),
        shippingDays: parseInt(countryData.shippingDays),
        orderBeforeHours: parseInt(countryData.hours),
        countryCode: countryData.countryCode.toUpperCase(),
        languageCode: this.shopifyLanguage.toUpperCase(),
        localization: `${this.shopifyLanguage.toLowerCase()}-${countryData.countryCode.toUpperCase()}`,
        timezone: countryData.timeZone,
        today: countryData.today,
        tomorrow: countryData.tomorrow,
        monday: countryData.monday,
        tuesday: countryData.tuesday,
        wednesday: countryData.wednesday,
        thursday: countryData.thursday,
        friday: countryData.friday,
        saturday: countryData.saturday,
        sunday: countryData.sunday,
        skipDays: countryData.noDelivery,
        skipShippingDays: countryData.noShipping,
        shipMonday: countryData.shipMonday,
        shipTuesday: countryData.shipTuesday,
        shipWednesday: countryData.shipWednesday,
        shipThursday: countryData.shipThursday,
        shipFriday: countryData.shipFriday,
        shipSaturday: countryData.shipSaturday,
        shipSunday: countryData.shipSunday,
      };

      return deliveryData;
    }

    /**
     * Set the delivery date
     */
    setDeliveryDate() {
      if (!this.deliveryData) {
        return;
      }

      // Clear the previous interval
      clearTimeout(this.deliveryDateTimeout);

      // Get today's date
      let today = new Date();

      // Use for testing to start a specific weekday
      // today.setDate(today.getDate() + 1);
      // console.log('deliveryData', this.deliveryData);
      // console.log('Today it is:', today);

      // Set the delivery date to current date
      let deliveryDate = today;

      // Array with all the shipping days
      let shippingDays = [];

      // Did we place the order before the order before hour
      let placeOrderAfterOrderBeforeHour = false;

      // Check if the time is before the order before hour else add a day
      if (today.getHours() >= this.deliveryData.orderBeforeHours) {
        shippingDays.push({
          date: new Date(deliveryDate),
          formattedDate: deliveryDate.toISOString().split('T')[0],
          skipReason: 'after cut off time',
        });
        placeOrderAfterOrderBeforeHour = true;
        deliveryDate.setDate(deliveryDate.getDate() + 1);
      }

      // Add the start shipping day offset to the delivery date
      for (let i = 0; i < this.deliveryData.shippingDays; i++) {
        shippingDays.push({
          date: new Date(deliveryDate),
          formattedDate: deliveryDate.toISOString().split('T')[0],
          skipReason: 'start shipping day offset',
        });
        deliveryDate.setDate(deliveryDate.getDate() + 1);
      }

      // Check if the start shipping day is a no shipping day
      const skipNoShippingDays = (shippingDay) => {
        // Skip if the day is already skipped as a skip day
        if (shippingDay.skipReason) {
          return false;
        }

        // Check if it's one of the skip days
        if (this.deliveryData.skipShippingDays.includes(shippingDay.formattedDate)) {
          return true;
        }

        // Get the weekday
        const weekday = shippingDay.date.toLocaleString('en', { weekday: 'long' }).toLowerCase();

        // Check if the weekday is a no delivery day
        const noShippingDays = {
          saturday: !this.deliveryData.shipSaturday,
          sunday: !this.deliveryData.shipSunday,
          monday: !this.deliveryData.shipMonday,
          tuesday: !this.deliveryData.shipTuesday,
          wednesday: !this.deliveryData.shipWednesday,
          thursday: !this.deliveryData.shipThursday,
          friday: !this.deliveryData.shipFriday,
        };

        // Return if the day is a no delivery day;
        return noShippingDays[weekday] || false;
      };

      // Check if the shipping start day is a no shipping day
      (() => {
        // Set the current start shipping day
        let date = {
          date: new Date(deliveryDate),
          formattedDate: deliveryDate.toISOString().split('T')[0],
          skipReason: null,
        };

        // Check if the day is a no shipping day, every time a day should be skipped.
        let shouldTestDay = true;
        while (shouldTestDay && shippingDays.length <= 30) {
          const shouldTestDay = skipNoShippingDays(date);

          // Stop while day if the day is not skipped
          if (!shouldTestDay) {
            return;
          }

          // Mark this day as skipped
          shippingDays.map((day) => {
            if (day.formattedDate === date.formattedDate) {
              day.skipReason = 'No start shipping day';
            }
          });

          // Add the delivery day to the shipping days if it's not already in the array
          if (!shippingDays.find((day) => day.formattedDate === date.formattedDate)) {
            date.skipReason = 'No start shipping day';
            shippingDays.push(date);
          }

          // Get the next date to test with in a new object
          const newDate = {};
          deliveryDate.setDate(deliveryDate.getDate() + 1);
          newDate.date = new Date(deliveryDate);
          newDate.formattedDate = newDate.date.toISOString().split('T')[0];
          newDate.skipReason = null;

          // Set the new date to test with in the next loop
          date = newDate;
        }
      })();

      // Add the delivery days offset to the delivery date
      for (let i = 0; i < this.deliveryData.deliveryDays; i++) {
        shippingDays.push({
          date: new Date(deliveryDate),
          formattedDate: deliveryDate.toISOString().split('T')[0],
          skipReason: null,
        });
        deliveryDate.setDate(deliveryDate.getDate() + 1);
      }

      // Add the current delivery day to the shipping days because it should be checked
      shippingDays.push({
        date: new Date(deliveryDate),
        formattedDate: deliveryDate.toISOString().split('T')[0],
        skipReason: null,
      });

      // Check if the delivery day is a no delivery day
      const skipNoDeliveryDays = (shippingDay) => {
        // Skip if the day is already skipped as a skip day
        if (shippingDay.skipReason) {
          return false;
        }

        // Check if it's one of the skip days
        if (this.deliveryData.skipDays.includes(shippingDay.formattedDate)) {
          return true;
        }

        // Get the weekday
        const weekday = shippingDay.date.toLocaleString('en', { weekday: 'long' }).toLowerCase();

        // Check if the weekday is a no delivery day
        const noDeliveryDays = {
          saturday: !this.deliveryData.saturday,
          sunday: !this.deliveryData.sunday,
          monday: !this.deliveryData.monday,
          tuesday: !this.deliveryData.tuesday,
          wednesday: !this.deliveryData.wednesday,
          thursday: !this.deliveryData.thursday,
          friday: !this.deliveryData.friday,
        };

        // Return if the day is a no delivery day;
        return noDeliveryDays[weekday] || false;
      };

      // Check for each shipping day if it's a no delivery day
      shippingDays.map((shippingDay) => {
        // Copy date object to prevent mutation issues-
        let date = { ...shippingDay };

        // Check if the day is a no delivery day, every time a day should be skipped.
        let shouldTestDay = true;
        while (shouldTestDay && shippingDays.length <= 30) {
          const shouldTestDay = skipNoDeliveryDays(date);

          // Stop while day if the day is not skipped
          if (!shouldTestDay) {
            return;
          }

          // Mark this day as skipped
          shippingDays.map((day) => {
            if (day.formattedDate === date.formattedDate) {
              day.skipReason = 'No delivery day';
            }
          });

          // Get the next date to test with
          const newDate = {};
          newDate.date = new Date(date.date);
          newDate.date.setDate(date.date.getDate() + 1);
          newDate.formattedDate = newDate.date.toISOString().split('T')[0];
          newDate.skipReason = null;
          deliveryDate.setDate(deliveryDate.getDate() + 1);

          // Add the delivery day to the shipping days if it's not already in the array
          if (!shippingDays.find((day) => day.formattedDate === newDate.formattedDate)) {
            shippingDays.push(newDate);
          }

          // Set the new date to test with in the next loop
          date = newDate;
        }
      });

      // Set the last day as the delivery date
      shippingDays[shippingDays.length - 1].skipReason = 'delivery day';

      // Used for testing
      // console.log('deliveryDate', deliveryDate);
      // console.log('shippingDays', shippingDays);

      // Set the expected delivery moment
      let deliveryMoment = deliveryDate.toLocaleString(this.deliveryData.localization, {
        weekday: 'long',
      });
      if (shippingDays.length == 0 && today != '') {
        deliveryMoment = this.deliveryData.today;
      } else if (shippingDays.length == 1 && this.deliveryData.tomorrow != '') {
        deliveryMoment = this.deliveryData.tomorrow;
      } else if (shippingDays.length >= 7) {
        deliveryMoment = deliveryDate.toLocaleString(this.deliveryData.localization, {
          day: 'numeric',
          month: 'long',
        });
      }

      // Set the current time
      const hour = deliveryDate.toLocaleString(this.deliveryData.localization, {
        hour: 'numeric',
      });
      const minutes = deliveryDate.toLocaleString(this.deliveryData.localization, {
        minute: 'numeric',
      });

      // Calculate the countdown hours and minutes
      let countDownHours = placeOrderAfterOrderBeforeHour
        ? 24 - hour + this.deliveryData.orderBeforeHours
        : this.deliveryData.orderBeforeHours - hour;
      if (minutes > 0) {
        countDownHours = countDownHours - 1;
      }
      if (countDownHours < 0) {
        countDownHours = 0;
      }
      let countDownMinutes = minutes > 0 ? 60 - minutes : 0;

      // Format the expected delivery text
      const text = this.expectedDeliveryText
        .replace('[moment]', deliveryMoment)
        .replace('[count-down-hours]', countDownHours)
        .replace('[count-down-minutes]', countDownMinutes);

      // Set the expected delivery text
      this.expectedDeliveryTextEl.textContent = text;

      // Start the check again after a half minute
      this.deliveryDateTimeout = setTimeout(() => {
        this.setDeliveryDate();
      }, 30000);
    }
  }

  customElements.define('expected-delivery', ExpectedDelivery);
}
